import {
  useParams as useReactRouterParams,
  useSearchParams as useReactRouterSearchParams,
} from "react-router-dom"
import { z } from "zod"

export interface UseRouteParamsArgs<
  PathShape extends z.ZodRawShape,
  SearchShape extends z.ZodRawShape,
> {
  path?: PathShape
  search?: SearchShape
}

/**
 * Validates react-router-dom path and search params.
 */
export function useRouteParams<
  PathShape extends z.ZodRawShape,
  SearchShape extends z.ZodRawShape,
>(args: UseRouteParamsArgs<PathShape, SearchShape>) {
  const { path = {} as PathShape, search = {} as SearchShape } = args

  const params = useReactRouterParams()
  const validatedPath = parseRouteParams(params, path)

  const [reactRouterSearchParams, setSearch] = useReactRouterSearchParams()
  const searchParams = Object.fromEntries(reactRouterSearchParams)
  const validatedSearch = parseRouteParams(searchParams, search)

  return {
    path: validatedPath,
    search: validatedSearch,
    setSearch,
  }
}

/**
 * Similar to useRouteParams but non hook-based because some react-router APIs like loaders don't use hooks.
 */
export function parseRouteParams<Shape extends z.ZodRawShape>(
  params: unknown,
  schema: Shape = {} as Shape,
) {
  const validated = z.object(schema).safeParse(params)

  if (!validated.success) throw new ParamsValidationError(validated.error)

  return validated.data
}

class ParamsValidationError extends Error {
  constructor(zodError: z.ZodError) {
    super(zodError.toString())
  }
}

export function isParamsValidationError(
  error: unknown,
): error is ParamsValidationError {
  return error instanceof ParamsValidationError
}
